import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import type {
  CompanyDetail,
  DocumentAudience,
  DocumentPublicationDetail,
  DocumentPublicationKind,
  DocumentTemplateListItem,
  EmployeeListItem,
  PublicationContractInput,
} from '@ip/shared-types';
import { DOCUMENT_AUDIENCES, DOCUMENT_PUBLICATION_KINDS } from '@ip/shared-types';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { DatePicker } from '@/components/shared/DatePicker';
import { cn } from '@/lib/utils';
import { useCompaniesApi, useDocumentsApi, useEmployeesApi } from '@/services/api';
import { t } from '@/i18n';

const PL_MAP: Record<string, string> = { ą: 'a', ć: 'c', ę: 'e', ł: 'l', ń: 'n', ó: 'o', ś: 's', ź: 'z', ż: 'z' };

function normalizeSearch(text: string): string {
  return text.toLowerCase().replace(/[ąćęłńóśźż]/g, (c) => PL_MAP[c] ?? c).replace(/[^a-z0-9]/g, '');
}

function employeeLabel(e: EmployeeListItem): string {
  return `${e.firstName} ${e.lastName}`;
}

interface PublishDialogProps {
  nip: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Accepts both list and detail template shapes — only these fields matter here. */
  template: Pick<
    DocumentTemplateListItem,
    'id' | 'name' | 'category' | 'currentVersion' | 'acceptanceMode' | 'defaultDeadlineDays' | 'templateKind'
  >;
  onPublished: (publication: DocumentPublicationDetail) => void;
}

/**
 * Publish-a-version dialog (Task 6, spec §8.4): campaign → audience, or
 * individual → one employee. `physical` acceptance mode requires
 * kind=individual (spec §3.5/Global Constraints) — the campaign option is
 * disabled with a hint in that case rather than validated only on submit.
 */
export function PublishDialog({ nip, open, onOpenChange, template, onPublished }: PublishDialogProps) {
  const { createPublication } = useDocumentsApi();
  const { list: listEmployees } = useEmployeesApi();
  const { getCompanyDetail } = useCompaniesApi();

  const physicalLocksIndividual = template.acceptanceMode === 'physical';
  // Umowa zlecenie template (umowa spec 2026-07-23 §5/§8b): always an
  // individual publication + an extra "contract data" section below.
  const isUmowa = template.templateKind === 'umowa_zlecenie';
  const lockIndividual = physicalLocksIndividual || isUmowa;

  const [kind, setKind] = useState<DocumentPublicationKind>('campaign');
  const [audience, setAudience] = useState<DocumentAudience>('all');
  const [employeeId, setEmployeeId] = useState<string | null>(null);
  const [deadline, setDeadline] = useState<Date | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeePickerOpen, setEmployeePickerOpen] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState('');

  // Contract data (umowa templates only).
  const [conclusionDate, setConclusionDate] = useState<Date | null>(null);
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [hourlyRate, setHourlyRate] = useState('');
  const [selectedRepIds, setSelectedRepIds] = useState<string[]>([]);
  const [writeRate, setWriteRate] = useState(true);
  const [company, setCompany] = useState<CompanyDetail | null>(null);
  const [companyLoading, setCompanyLoading] = useState(false);
  // Fetch failure is NOT "representation not configured" — it blocks publish
  // with its own message + retry (bumping the counter re-runs the effect).
  const [companyError, setCompanyError] = useState(false);
  const [companyFetchAttempt, setCompanyFetchAttempt] = useState(0);

  useEffect(() => {
    if (!open) return;
    setKind(lockIndividual ? 'individual' : 'campaign');
    setAudience('all');
    setEmployeeId(null);
    setEmployeeSearch('');
    setDeadline(
      template.defaultDeadlineDays ? dayjs().add(template.defaultDeadlineDays, 'day').toDate() : null,
    );
    setConclusionDate(new Date());
    setDateFrom(null);
    setDateTo(null);
    setHourlyRate('');
    setSelectedRepIds([]);
    setWriteRate(true);
    setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, template.id]);

  // Company representation (mode + representatives) feeds the picker below.
  // `company` is cleared before each fetch so a failed re-open never shows a
  // previous open's (possibly stale, replaced-ids) representatives.
  useEffect(() => {
    if (!open || !isUmowa) return;
    let cancelled = false;
    setCompany(null);
    setCompanyError(false);
    setCompanyLoading(true);
    getCompanyDetail(nip)
      .then((detail) => { if (!cancelled) setCompany(detail); })
      .catch(() => { if (!cancelled) setCompanyError(true); })
      .finally(() => { if (!cancelled) setCompanyLoading(false); });
    return () => { cancelled = true; };
  }, [open, isUmowa, nip, getCompanyDetail, companyFetchAttempt]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setEmployeesLoading(true);
    listEmployees(nip, { pageSize: 200 })
      .then((res) => { if (!cancelled) setEmployees(res.items); })
      .catch(() => { if (!cancelled) toast.error(t('errors.loadingFailed')); })
      .finally(() => { if (!cancelled) setEmployeesLoading(false); });
    return () => { cancelled = true; };
  }, [open, nip, listEmployees]);

  const selectedEmployee = useMemo(
    () => employees.find((e) => e.id === employeeId) ?? null,
    [employees, employeeId],
  );

  const filteredEmployees = useMemo(() => {
    if (!employeeSearch) return employees;
    const needle = normalizeSearch(employeeSearch);
    return employees.filter((e) => normalizeSearch(employeeLabel(e)).includes(needle));
  }, [employees, employeeSearch]);

  // 'one_person' → exactly 1 signer, 'two_person' → exactly 2; null = the
  // company's representation is not configured yet → publishing is blocked.
  const requiredReps =
    company?.representationMode === 'two_person' ? 2
      : company?.representationMode === 'one_person' ? 1
        : null;
  const representatives = company?.representatives ?? [];
  const representationConfigured =
    requiredReps !== null && representatives.length >= requiredReps;

  const toggleRepresentative = (id: string) => {
    setSelectedRepIds((prev) => {
      if (prev.includes(id)) return prev.filter((r) => r !== id);
      // Single-signer mode behaves like a radio; two-signer mode caps at 2
      // (order of selection = signing order, mirrored to the backend).
      if (requiredReps === 1) return [id];
      if (requiredReps !== null && prev.length >= requiredReps) return prev;
      return [...prev, id];
    });
  };

  const buildContract = (): PublicationContractInput | null => {
    if (!representationConfigured) {
      toast.error(t('documents.publications.contract.notConfigured'));
      return null;
    }
    if (!conclusionDate || !dateFrom || !dateTo) {
      toast.error(t('documents.publications.contract.datesRequired'));
      return null;
    }
    const from = dayjs(dateFrom).format('YYYY-MM-DD');
    const to = dayjs(dateTo).format('YYYY-MM-DD');
    if (from > to) {
      toast.error(t('documents.publications.contract.dateOrder'));
      return null;
    }
    const rateText = hourlyRate.trim();
    if (!rateText) {
      toast.error(t('documents.publications.contract.rateRequired'));
      return null;
    }
    // Comma or dot decimal, max 2 decimal places — mirrors the backend's
    // maxDecimalPlaces:2 validation so the PDF and kartoteka always agree.
    if (!/^\d+([.,]\d{1,2})?$/.test(rateText)) {
      toast.error(t('documents.publications.contract.rateFormat'));
      return null;
    }
    const rate = Number(rateText.replace(',', '.'));
    if (!Number.isFinite(rate) || rate <= 0) {
      toast.error(t('documents.publications.contract.rateFormat'));
      return null;
    }
    if (selectedRepIds.length !== requiredReps) {
      toast.error(
        t('documents.publications.contract.selectExactly', { count: requiredReps ?? 1 }),
      );
      return null;
    }
    return {
      conclusionDate: dayjs(conclusionDate).format('YYYY-MM-DD'),
      dateFrom: from,
      dateTo: to,
      hourlyRate: rate,
      representativeIds: selectedRepIds,
      writeRateToKartoteka: writeRate,
    };
  };

  const handleSubmit = async () => {
    if (kind === 'campaign' && !audience) {
      toast.error(t('documents.publications.audienceRequired'));
      return;
    }
    if (kind === 'individual' && !employeeId) {
      toast.error(t('documents.publications.employeeRequired'));
      return;
    }
    let contract: PublicationContractInput | undefined;
    if (isUmowa) {
      const built = buildContract();
      if (!built) return;
      contract = built;
    }
    try {
      setSubmitting(true);
      const result = await createPublication(nip, {
        templateId: template.id,
        kind,
        audience: kind === 'campaign' ? audience : undefined,
        employeeId: kind === 'individual' ? (employeeId ?? undefined) : undefined,
        deadline: deadline ? dayjs(deadline).format('YYYY-MM-DD') : undefined,
        contract,
      });
      toast.success(t('documents.publications.published', { number: result.number }));
      // The publication succeeded but the kartoteka rate write did not —
      // surface the backend's reason without failing the flow.
      if (result.rateWarning) {
        toast.warning(
          t('documents.publications.contract.rateWarning', { message: result.rateWarning }),
        );
      }
      onPublished(result);
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('documents.publications.publishError'));
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className={cn('sm:max-w-md', isUmowa && 'max-h-[90vh] overflow-y-auto sm:max-w-lg')}>
        <DialogHeader>
          <DialogTitle>{t('documents.publications.publishDialogTitle')}</DialogTitle>
          <DialogDescription>{template.name}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="space-y-1.5">
            <Label className="text-muted-foreground text-xs">{t('documents.publications.kind')}</Label>
            <RadioGroup
              value={kind}
              onValueChange={(v) => setKind(v as DocumentPublicationKind)}
              className="grid-cols-2"
            >
              {DOCUMENT_PUBLICATION_KINDS.map((k) => (
                <Label
                  key={k}
                  htmlFor={`pub-kind-${k}`}
                  className={cn(
                    'flex cursor-pointer items-center gap-2 rounded-md border p-2 text-sm font-normal',
                    kind === k && 'border-primary bg-primary/5',
                    k === 'campaign' && lockIndividual && 'cursor-not-allowed opacity-50',
                  )}
                >
                  <RadioGroupItem
                    id={`pub-kind-${k}`}
                    value={k}
                    disabled={k === 'campaign' && lockIndividual}
                  />
                  {t(`documents.publications.kinds.${k}`)}
                </Label>
              ))}
            </RadioGroup>
            {physicalLocksIndividual && (
              <p className="text-muted-foreground text-xs">
                {t('documents.publications.physicalCampaignHint')}
              </p>
            )}
            {isUmowa && !physicalLocksIndividual && (
              <p className="text-muted-foreground text-xs">
                {t('documents.publications.contract.individualHint')}
              </p>
            )}
          </div>

          {kind === 'campaign' ? (
            <div className="space-y-1.5">
              <Label className="text-muted-foreground text-xs">{t('documents.publications.audience')}</Label>
              <Select value={audience} onValueChange={(v) => setAudience(v as DocumentAudience)}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_AUDIENCES.map((a) => (
                    <SelectItem key={a} value={a}>
                      {t(`documents.templates.audiences.${a}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label className="text-muted-foreground text-xs">{t('documents.publications.employee')}</Label>
              <Popover open={employeePickerOpen} onOpenChange={setEmployeePickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={employeePickerOpen}
                    disabled={employeesLoading}
                    className="w-full justify-between font-normal"
                  >
                    <span className="truncate">
                      {employeesLoading ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : selectedEmployee ? (
                        employeeLabel(selectedEmployee)
                      ) : (
                        t('documents.publications.employeeSearchPlaceholder')
                      )}
                    </span>
                    <ChevronsUpDown className="size-3.5 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder={t('documents.publications.employeeSearchPlaceholder')}
                      value={employeeSearch}
                      onValueChange={setEmployeeSearch}
                    />
                    <CommandList>
                      <CommandEmpty>{t('documents.publications.noEmployees')}</CommandEmpty>
                      <CommandGroup>
                        {filteredEmployees.map((e) => (
                          <CommandItem
                            key={e.id}
                            value={e.id}
                            onSelect={() => {
                              setEmployeeId(e.id);
                              setEmployeePickerOpen(false);
                            }}
                          >
                            <Check className={cn('size-4', employeeId === e.id ? 'opacity-100' : 'opacity-0')} />
                            <span className="truncate">{employeeLabel(e)}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}

          {isUmowa && (
            <div className="space-y-3 rounded-md border p-3">
              <p className="text-sm font-medium">{t('documents.publications.contract.section')}</p>

              <div className="space-y-1.5">
                <Label className="text-muted-foreground text-xs">
                  {t('documents.publications.contract.conclusionDate')}
                </Label>
                <DatePicker value={conclusionDate} onChange={setConclusionDate} />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-muted-foreground text-xs">
                    {t('documents.publications.contract.dateFrom')}
                  </Label>
                  <DatePicker value={dateFrom} onChange={setDateFrom} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-muted-foreground text-xs">
                    {t('documents.publications.contract.dateTo')}
                  </Label>
                  <DatePicker value={dateTo} onChange={setDateTo} />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="contract-rate" className="text-muted-foreground text-xs">
                  {t('documents.publications.contract.hourlyRate')}
                </Label>
                {/* Plain text + inputMode=decimal (not type=number): a number
                    input never exposes a comma decimal through target.value,
                    which the replace(',', '.') parsing in buildContract needs. */}
                <Input
                  id="contract-rate"
                  type="text"
                  inputMode="decimal"
                  placeholder="30,50"
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(e.target.value)}
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-muted-foreground text-xs">
                  {t('documents.publications.contract.representation')}
                </Label>
                {companyLoading ? (
                  <Loader2 className="text-muted-foreground size-4 animate-spin" />
                ) : companyError ? (
                  <div className="flex items-center justify-between gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-2.5 py-1.5">
                    <p className="text-destructive text-xs">
                      {t('documents.publications.contract.loadFailed')}
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 shrink-0 text-xs"
                      onClick={() => setCompanyFetchAttempt((n) => n + 1)}
                    >
                      {t('documents.publications.contract.retry')}
                    </Button>
                  </div>
                ) : !representationConfigured ? (
                  <p className="rounded-md border border-amber-500/50 bg-amber-500/10 px-2.5 py-1.5 text-xs text-amber-700 dark:text-amber-400">
                    {t('documents.publications.contract.notConfigured')}
                  </p>
                ) : (
                  <>
                    <p className="text-muted-foreground text-xs">
                      {t(`documents.publications.contract.modes.${company!.representationMode!}`)}
                      {' — '}
                      {t('documents.publications.contract.selectExactly', { count: requiredReps ?? 1 })}
                    </p>
                    {requiredReps !== null && requiredReps > 1 && (
                      <p className="text-muted-foreground text-xs">
                        {t('documents.publications.contract.orderHint')}
                      </p>
                    )}
                    <div className="flex flex-col gap-1.5">
                      {representatives.map((rep) => {
                        const id = rep.id ?? '';
                        const checked = selectedRepIds.includes(id);
                        // Selection order = signing order (mirrored to the
                        // backend) — surface it as a 1./2. badge in two-signer
                        // mode so the sequence is never invisible.
                        const order =
                          checked && requiredReps !== null && requiredReps > 1
                            ? selectedRepIds.indexOf(id) + 1
                            : null;
                        const atLimit =
                          requiredReps !== null &&
                          requiredReps > 1 &&
                          selectedRepIds.length >= requiredReps;
                        return (
                          <Label
                            key={id}
                            className={cn(
                              'flex cursor-pointer items-center gap-2 rounded-md border p-2 text-sm font-normal',
                              checked && 'border-primary bg-primary/5',
                              !checked && atLimit && 'cursor-not-allowed opacity-50',
                            )}
                          >
                            <Checkbox
                              checked={checked}
                              disabled={!checked && atLimit}
                              onCheckedChange={() => toggleRepresentative(id)}
                            />
                            {order !== null && (
                              <Badge variant="outline" className="border-primary/50 shrink-0 px-1.5">
                                {order}.
                              </Badge>
                            )}
                            <span className="min-w-0 flex-1 truncate">{rep.fullName}</span>
                            <span className="text-muted-foreground shrink-0 text-xs">{rep.title}</span>
                          </Label>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              <Label className="flex cursor-pointer items-center gap-2 text-sm font-normal">
                <Checkbox
                  checked={writeRate}
                  onCheckedChange={(checked) => setWriteRate(checked === true)}
                />
                {t('documents.publications.contract.writeRate')}
              </Label>
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-muted-foreground text-xs">{t('documents.publications.deadline')}</Label>
            <DatePicker value={deadline} onChange={setDeadline} placeholder={t('documents.publications.noDeadline')} />
          </div>

          <div className="flex items-center justify-between">
            <Label className="text-muted-foreground text-xs">{t('documents.publications.acceptanceMode')}</Label>
            <Badge variant="outline">
              {t(`documents.templates.acceptances.${template.acceptanceMode}`)}
            </Badge>
          </div>
        </div>

        <DialogFooter className="pt-2">
          <Button type="button" variant="outline" disabled={submitting} onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            disabled={submitting || (isUmowa && (companyLoading || companyError || !representationConfigured))}
            onClick={handleSubmit}
          >
            {submitting && <Loader2 className="size-4 animate-spin" />}
            {t('documents.publications.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
